import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TenantResponse } from './tenant.model';
import { LogService } from '../shared/services/log.service';
import { GlobalService } from '../shared/services/global.service';
import { SimpleTenant } from '../shared/models/simple-tenant.model';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  apiHost: string = environment.apiHost;

  constructor(
    private http: HttpClient,
    private logService: LogService,
    private globalService: GlobalService
  ) {}  

  getTenantForHostname(location: Location): string {
    if (location.host.split('.')[0].toLowerCase().trim() != 'main')
      return location.host.split('.')[0];
    if (location.pathname.split('/')[1] == '404' || location.pathname.toLowerCase().split('/')[1] == 'home')
      return 'main';
    return location.pathname.split('/')[1];
  }

  getTenantForString(s: string) {
    return s;
  }

  getTenantForHost(host: string): string {
    return this.getTenantForString(host);
  }  

  getTenant(): string {
    return this.getTenantForHostname(location);
  }

  addTenantToHeaders(headers: HttpHeaders): HttpHeaders {
    var tenant = this.getTenant();
    if (tenant == '')
      tenant = localStorage.getItem('tenant');
    this.globalService.currentTenant = tenant;
    if (tenant) return headers.append('X-Tenant-ID', tenant);
    else return headers;
  }  

  async getLanguage() {
    if (
      this.globalService.currentLanguage != null &&
      this.globalService.currentLanguage != '' &&
      this.globalService.currentLanguage != '0'
    )
      return this.globalService.currentLanguage;
    var tenant = await this.getSimpleTenant();
    return tenant.language;
  }

  getDioceseTenants(): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetDioceseTenants?dioceseSubdomain=` + this.getTenant();
      this.http
        .post<TenantResponse>(apiURL,null)
        .toPromise()  
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantResponse',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getTenantData(): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetTenant?subDomain=` + this.getTenant();
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantResponse',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise; 
  }

  getSimpleTenant(): Promise<SimpleTenant> {
    let promise = new Promise<SimpleTenant>((resolve, reject) => {
      if (this.globalService.subdomain == this.getTenant() && this.globalService.simpleTenant != null)
        return resolve(this.globalService.simpleTenant);
      let apiURL =
        `${this.apiHost}/Tenant/GetSimpleTenantData?subDomain=` + this.getTenant();
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            this.globalService.subdomain = this.getTenant();
            this.globalService.simpleTenant = res['tenant'];
            resolve(res['tenant']);  
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Simple Tenant',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });  
    return promise;
  }

  getTenantById(tenantId: number): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetTenantById?tenantId=` + tenantId;
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);  
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantById',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getAllTenants(): Promise<TenantResponse[]> {
    let promise = new Promise<TenantResponse[]>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetAllTenants`;
      this.http
        .get<TenantResponse[]>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetAllTenants',
                msg  
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

    getAllTenantNames(): Promise<string[]> {
    let promise = new Promise<string[]>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetAllTenantNames`;
      this.http
        .get<string[]>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetAllTenantNames',
                msg  
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  addTenant(tenant: TenantResponse): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/addTenant`;
      this.http
        .post<TenantResponse>(apiURL, tenant)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Add Tenant',
                msg  
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;  
  }

  updateTenant(tenant: TenantResponse, checkNewSecrets: boolean): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/tenant/UpdateTenant?RefreshSecrets=`+checkNewSecrets ;   
      this.http
        .post<TenantResponse>(apiURL, tenant)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)  
              this.logService.addToLog(
                'Tenant Service: Update Tenant',
                msg  
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }
  
  getTenantLogo(subdomain: string): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/file/GetLogo?subdomain=` + subdomain;
      this.http
        .get<string>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res['imageUrl']);    
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Tenant Logo',
                msg
              );
            reject('');
          }
        );  
    });
    return promise;
  }

  tenantExists(subdomain: string): Promise<boolean> {
    let promise = new Promise<boolean>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/tenant/TenantExists?subdomain=` + subdomain;
      this.http
        .get<boolean>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);    
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Tenant Exists failing',
                msg
              );
            reject('');
          }  
        );  
    });
    return promise;
  }

  getTenantSmallLogo(subdomain: string): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/file/GetSmallLogo?subdomain=` + subdomain;
      this.http
        .get<string>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res['imageUrl']);    
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Tenant Small Logo',
                msg
              );
            reject('');
          }  
        );  
    });
    return promise;
  }
}
